import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { App } from "antd";
import _ from "lodash";

import * as api from "apis/translation";
import { setApp } from "redux/configSlice";
import { useTranslationSelector } from "hooks/useSelector";

export function useTranslations(options = {}) {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useQuery(
    ["translations"],
    async () => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getTranslations();
        response = data;
      } catch (error) {}
      return response;
    },
    {
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
}

export function useTranslationsDefault(options = {}) {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useQuery(
    ["translations/default"],
    async () => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getTranslationsDefault();
        response = data;
      } catch (error) {}
      return response;
    },
    {
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
}

export const useResetTranslations = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  return useMutation(() => api.resetTranslations(), {
    onMutate: async () => {
      await queryClient.cancelQueries(["translations"]);
      const prev = queryClient.getQueryData(["translations"]);
      const defaultTranslations = queryClient.getQueryData([
        "translations/default",
      ]);
      if (!_.isEmpty(defaultTranslations?.data)) {
        queryClient.setQueryData(["translations"], (old) => ({
          ...defaultTranslations?.data,
        }));
        dispatch(setApp({ translation: defaultTranslations?.data }));
      }
      return { prev };
    },
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error, payload, context) => {
      const { prev } = context;
      if (prev) {
        queryClient.setQueryData(["translations"], prev);
      }
      message.error(t("error.failed_save_data_to_server"));
    },
    onSettled: () => {
      queryClient.invalidateQueries(["translations"]);
    },
  });
};

export const useUpdateTranslation = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const translation = useTranslationSelector();

  return useMutation((payload) => api.updateTranslation(payload), {
    onMutate: async (payload) => {
      await queryClient.cancelQueries(["translations"]);
      const prev = queryClient.getQueryData(["translations"]);
      const prevTranslation = _.cloneDeep(translation);
      let nextData = {};
      queryClient.setQueryData(["translations"], (old) => {
        let oldData = _.cloneDeep(old);
        const { name, category, value } = payload;
        const keys = category.split(".");
        _.set(oldData, [...keys, name], value);
        nextData = oldData;
        return oldData;
      });
      nextData.menu = nextData?.hr_menu;
      delete nextData.hr_menu;
      dispatch(setApp({ translation: nextData }));

      return { prev, prevTranslation };
    },

    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error, payload, context) => {
      const { prev, prevTranslation } = context;
      if (prev) {
        queryClient.setQueryData(["translations"], prev);
      }
      dispatch(setApp({ translation: prevTranslation }));
      message.error(t("error.failed_save_data_to_server"));
    },
    onSettled: () => {
      queryClient.invalidateQueries(["translations"]);
    },
  });
};
